/**
 * timestamps — one reader for the date/time shapes a Practice Pulse card can hold.
 *
 * WHY THIS EXISTS (2026-10-30). CheckoutNotes.vue read every card timestamp as `x.seconds * 1000`,
 * which assumes a Firestore Timestamp. The board also holds plain JS Dates: PracticePulse.vue sets
 * `dateTimeTreatmentStarted = moment().toDate()` locally and writes the SAME object to the card until
 * the Firestore snapshot round-trips back. A Date has no `.seconds`, so `undefined * 1000` became an
 * Invalid Date and went to the database as NULL. (And `dateTimeCheckout` used `* 100`, a different bug.)
 *
 * toJsDate accepts every shape and returns a VALID Date or null — never an Invalid Date, so a
 * caller can use `toJsDate(x) || fallback` and what is sent is either a real instant or an explicit null.
 *
 * ⚠️ Keep this file import-free. The unit tests execute this exact file (same rule as
 *    src/utils/waitElapsed.js and src/utils/phiMask.js). It overlaps src/utils/firestoreTime.js
 *    `coerceToMs`, which does not read the plain `{seconds, nanoseconds}` shape and returns ms, not a Date.
 */

function validDate(d) {
  return d instanceof Date && !isNaN(d.getTime()) ? d : null;
}

/**
 * @param {*} v Firestore Timestamp (has toDate) | {seconds, nanoseconds} | {_seconds, _nanoseconds}
 *              | Date | ISO/date string | epoch-millisecond number | null | undefined
 * @returns {Date|null} a valid Date, or null when the value is absent or cannot be read as an instant.
 */
export function toJsDate(v) {
  if (v === null || v === undefined) return null;

  if (v instanceof Date) return validDate(v);

  if (typeof v === 'number') return Number.isFinite(v) ? validDate(new Date(v)) : null;

  if (typeof v === 'string') return v.trim() === '' ? null : validDate(new Date(v));

  if (typeof v === 'object') {
    // Firestore Timestamp (and moment objects, which also expose toDate)
    if (typeof v.toDate === 'function') {
      try { return validDate(v.toDate()); } catch (e) { return null; }
    }
    // plain / serialized Timestamp
    const seconds = typeof v.seconds === 'number' ? v.seconds : v._seconds;
    if (typeof seconds === 'number' && Number.isFinite(seconds)) {
      const nanos = typeof v.nanoseconds === 'number' ? v.nanoseconds
        : (typeof v._nanoseconds === 'number' ? v._nanoseconds : 0);
      return validDate(new Date(seconds * 1000 + Math.floor(nanos / 1e6)));
    }
  }

  return null;
}
